import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Typography,
  Paper,
  Divider,
  Box,
} from "@material-ui/core";
import GavelIcon from "@material-ui/icons/Gavel";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
    overflowY: "auto",
    backgroundColor: "#f5f7fa",
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(8),
  },
  paper: {
    padding: theme.spacing(5),
    borderRadius: "16px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  icon: {
    fontSize: 40,
    color: theme.palette.primary.main,
  },
  title: {
    fontWeight: 700,
    color: "#1a1a2e",
  },
  subtitle: {
    color: "#6b7280",
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontWeight: 600,
    color: "#1a1a2e",
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
  },
  body: {
    color: "#374151",
    lineHeight: 1.8,
    marginBottom: theme.spacing(1.5),
  },
  divider: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(3),
  },
  footer: {
    marginTop: theme.spacing(5),
    padding: theme.spacing(2.5),
    backgroundColor: "#f0f4ff",
    borderRadius: "12px",
    borderLeft: "4px solid " + theme.palette.primary.main,
  },
  footerText: {
    color: "#374151",
    fontSize: "0.9rem",
  },
  backLink: {
    display: "inline-block",
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
    textDecoration: "none",
    fontWeight: 500,
    fontSize: "0.95rem",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const TermsOfUse = () => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <Container maxWidth="md">
        <a href="/login" className={classes.backLink}>
          ← Voltar para o login
        </a>

        <Paper className={classes.paper}>
          <div className={classes.header}>
            <GavelIcon className={classes.icon} />
            <Typography variant="h4" component="h1" className={classes.title}>
              Termos de Uso
            </Typography>
          </div>

          <Typography variant="body2" className={classes.subtitle}>
            ZapConverse — Última atualização: 27 de abril de 2026
          </Typography>

          <Divider />

          <Typography variant="body1" className={classes.body} style={{ marginTop: 24 }}>
            Bem-vindo(a) à <strong>ZapConverse</strong>. Ao acessar e utilizar nossa plataforma
            de chatbot e atendimento automatizado via WhatsApp, você concorda com os presentes Termos de Uso.
            Leia-os atentamente antes de utilizar os serviços. Caso não concorde com alguma disposição, não
            utilize a plataforma.
          </Typography>

          {/* Seção 1 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            1. Descrição do Serviço
          </Typography>
          <Typography variant="body1" className={classes.body}>
            A ZapConverse oferece uma plataforma SaaS (Software as a Service) para automação de
            atendimento ao cliente via WhatsApp, utilizando a <strong>WhatsApp Business API (Cloud API)</strong>{" "}
            fornecida pela Meta Platforms, Inc. Os serviços incluem:
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>Criação e gerenciamento de chatbots para atendimento automatizado;</li>
              <li>Gerenciamento de conversas e filas de atendimento;</li>
              <li>Envio e recebimento de mensagens via WhatsApp Business API;</li>
              <li>Campanhas de mensagens e contatos em massa (sujeito às políticas da Meta);</li>
              <li>Relatórios e análise de desempenho de atendimento;</li>
              <li>Integrações com sistemas externos via API e webhooks.</li>
            </ul>
          </Typography>

          {/* Seção 2 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            2. Condições de Uso
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Para utilizar a plataforma, o usuário deve:
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>Ter capacidade legal para contratar, nos termos da legislação brasileira;</li>
              <li>Fornecer informações verdadeiras, completas e atualizadas no cadastro;</li>
              <li>Manter a confidencialidade de suas credenciais de acesso;</li>
              <li>Utilizar a plataforma exclusivamente para fins lícitos e em conformidade com estes Termos;</li>
              <li>Cumprir as políticas de uso do WhatsApp Business e da Meta Platforms.</li>
            </ul>
          </Typography>

          {/* Seção 3 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            3. Responsabilidades do Usuário
          </Typography>
          <Typography variant="body1" className={classes.body}>
            O usuário é integralmente responsável por:
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>Todo o conteúdo publicado, enviado ou compartilhado por meio da plataforma;</li>
              <li>Obter o consentimento adequado dos destinatários das mensagens, conforme a LGPD e as políticas do WhatsApp;</li>
              <li>Não utilizar a plataforma para enviar spam, conteúdo ofensivo, ilegal ou enganoso;</li>
              <li>Não realizar engenharia reversa, descompilar ou tentar extrair o código-fonte da plataforma;</li>
              <li>Manter seus dados de acesso em segurança, sendo responsável por qualquer uso não autorizado de sua conta;</li>
              <li>Cumprir todas as leis e regulamentações aplicáveis, incluindo a LGPD e as políticas da Meta.</li>
            </ul>
          </Typography>

          {/* Seção 4 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            4. Uso da API Oficial do WhatsApp (Cloud API da Meta)
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Nossa plataforma opera exclusivamente por meio da <strong>WhatsApp Business API (Cloud API)</strong>,
            solução oficial da Meta. O uso da plataforma está sujeito às{" "}
            <a href="https://www.whatsapp.com/legal/business-policy" target="_blank" rel="noopener noreferrer">
              Políticas Comerciais do WhatsApp
            </a>{" "}
            e aos{" "}
            <a href="https://developers.facebook.com/terms" target="_blank" rel="noopener noreferrer">
              Termos da Plataforma da Meta
            </a>.
          </Typography>
          <Typography variant="body1" className={classes.body}>
            O usuário reconhece que:
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>A Meta pode impor limites de mensagens, suspender ou bloquear números que violem suas políticas;</li>
              <li>O envio de mensagens não solicitadas (spam) pode resultar no banimento permanente do número;</li>
              <li>A aprovação de templates de mensagem está sujeita à revisão e aprovação da Meta;</li>
              <li>A ZapConverse não é responsável por decisões tomadas pela Meta em relação às contas do usuário.</li>
            </ul>
          </Typography>

          {/* Seção 5 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            5. Limitações de Responsabilidade
          </Typography>
          <Typography variant="body1" className={classes.body}>
            A ZapConverse não se responsabiliza por:
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>Interrupções, falhas ou indisponibilidades da plataforma causadas por fatores fora de nosso controle, como falhas na infraestrutura da Meta ou de provedores de internet;</li>
              <li>Perdas ou danos decorrentes do uso indevido da plataforma pelo usuário;</li>
              <li>Conteúdo de mensagens enviadas pelos usuários a seus clientes;</li>
              <li>Penalidades impostas pela Meta em razão de violações das políticas do WhatsApp;</li>
              <li>Danos indiretos, incidentais ou consequentes resultantes do uso ou impossibilidade de uso da plataforma.</li>
            </ul>
          </Typography>
          <Typography variant="body1" className={classes.body}>
            A responsabilidade total da ZapConverse, em qualquer hipótese, fica limitada ao
            valor pago pelo usuário nos últimos 3 (três) meses de utilização da plataforma.
          </Typography>

          {/* Seção 6 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            6. Propriedade Intelectual
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Todos os direitos de propriedade intelectual relativos à plataforma, incluindo software, design,
            marca, logotipo e conteúdo, pertencem exclusivamente à ZapConverse ou a seus
            licenciadores. É vedado ao usuário:
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>Copiar, reproduzir ou distribuir qualquer elemento da plataforma sem autorização prévia por escrito;</li>
              <li>Utilizar a marca ou identidade visual da ZapConverse sem autorização;</li>
              <li>Sublicenciar ou ceder os direitos de uso da plataforma a terceiros.</li>
            </ul>
          </Typography>
          <Typography variant="body1" className={classes.body}>
            O usuário retém todos os direitos sobre o conteúdo que publica na plataforma, concedendo à
            ZapConverse uma licença limitada para processar tal conteúdo exclusivamente para
            a prestação dos serviços contratados.
          </Typography>

          {/* Seção 7 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            7. Planos, Pagamentos e Cancelamento
          </Typography>
          <Typography variant="body1" className={classes.body}>
            O acesso à plataforma está condicionado à contratação de um plano de uso, conforme os valores
            e condições disponibilizados no momento da assinatura. O usuário pode cancelar sua assinatura
            a qualquer momento, observando que:
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>O cancelamento interrompe o acesso ao final do período já pago;</li>
              <li>Não há reembolso proporcional por período não utilizado, salvo disposição legal em contrário;</li>
              <li>A ZapConverse reserva-se o direito de suspender ou encerrar o acesso em caso de inadimplência ou violação destes Termos;</li>
              <li>Dados poderão ser excluídos após 30 dias do cancelamento, conforme nossa Política de Privacidade.</li>
            </ul>
          </Typography>

          {/* Seção 8 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            8. Alterações nos Termos
          </Typography>
          <Typography variant="body1" className={classes.body}>
            A ZapConverse reserva-se o direito de alterar estes Termos de Uso a qualquer
            momento, mediante notificação prévia aos usuários por meio da plataforma ou e-mail cadastrado.
            O uso continuado da plataforma após as alterações implica na aceitação dos novos Termos.
          </Typography>

          {/* Seção 9 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            9. Foro e Legislação Aplicável
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Estes Termos de Uso são regidos e interpretados de acordo com as leis da República Federativa
            do Brasil, em especial a <strong>Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018)</strong>{" "}
            e o <strong>Código de Defesa do Consumidor (Lei nº 8.078/1990)</strong>, quando aplicável.
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Fica eleito o foro da comarca de domicílio do usuário, ou, na sua impossibilidade, o foro da
            sede da ZapConverse, para dirimir quaisquer controvérsias decorrentes destes Termos.
          </Typography>

          {/* Seção 10 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            10. Contato
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Em caso de dúvidas, reclamações ou solicitações relacionadas a estes Termos de Uso, entre em
            contato conosco:
          </Typography>
          <Typography variant="body1" className={classes.body}>
            <strong>E-mail:</strong>{" "}
            <a href="mailto:contato@zapconverse.com">contato@zapconverse.com</a>
          </Typography>

          <Divider className={classes.divider} />

          <Box className={classes.footer}>
            <Typography variant="body2" className={classes.footerText}>
              <strong>ZapConverse</strong> — Estes Termos entram em vigor em 27 de abril de 2026.
              Dúvidas? Entre em contato: <a href="mailto:contato@zapconverse.com">contato@zapconverse.com</a>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </div>
  );
};

export default TermsOfUse;
